import { z } from "zod";
import { ufSchema } from "./calculation.js";
/**
 * Governance helpers for the next DRAFT ruleset (ADR 0011 adoption) and for
 * rule proposal drafts prefilled from certified fiscal facts (ADR 0013 §4).
 *
 * A proposal draft is NOT a rule proposal. It holds a prefilled, possibly
 * incomplete fiscal rule and a checklist of the official documents to attach.
 * Submitting it runs the ordinary `POST /v1/rule-proposals` path: approved
 * evidence is required, the proposal starts PENDING_REVIEW and review,
 * approval and publication are unchanged. Nothing here approves or publishes.
 */
export const TAX_POLICY_VERSIONS = [
    "legacy-v1",
    "derived-mva-v1",
    "tax-family-v1",
    "tax-family-v2",
];
export const taxPolicyVersionSchema = z.enum(TAX_POLICY_VERSIONS);
const ncmSchema = z.string().regex(/^\d{8}$/);
const cestSchema = z.string().regex(/^\d{2}\.\d{3}\.\d{2}$/);
const rateSchema = z.string().regex(/^(0|[1-9]\d{0,5})(\.\d{1,8})?$/);
const dateSchema = z.string().date();
const reasonSchema = z.string().trim().min(10).max(2_000);
/**
 * Legal basis (provenance) of an effective internal ICMS burden: the rule that
 * lowers the destination substitute's burden on these goods, e.g. RICMS/SP
 * Anexo II art. 39 (12 percent for foods). Dates bound the benefit; outside
 * them the engine falls back to the nominal rate and flags it.
 */
export const effectiveInternalRateBasisSchema = z
    .object({
    legalReference: z.string().trim().min(5).max(500),
    sourceUrl: z.string().url().max(2_000).optional(),
    validFrom: dateSchema.optional(),
    validTo: dateSchema.optional(),
    note: z.string().trim().min(1).max(1_000).optional(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.validFrom !== undefined &&
        value.validTo !== undefined &&
        value.validTo < value.validFrom) {
        context.addIssue({
            code: "custom",
            path: ["validTo"],
            message: "Must not precede validFrom",
        });
    }
});
/* -- Next draft ruleset ------------------------------------------------------ */
/** `POST /v1/rulesets/drafts` (PUBLISH:TAX_RULE:ALL, Idempotency-Key). */
export const rulesetDraftCreateSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    policyVersion: taxPolicyVersionSchema,
    reason: reasonSchema,
})
    .strict();
/**
 * Why a route rule marks IPI `EXCLUDED`. `PLACEHOLDER`: the stated reason
 * says the source did not quote TIPI, so under tax-family-v1 IPI correctly
 * comes from the TIPI row. `REVIEW`: any other reason; it may be a reviewed
 * treatment that must be restated as `EXEMPT` before adopting tax-family-v1.
 */
export const ipiExclusionKindSchema = z.enum(["PLACEHOLDER", "REVIEW"]);
export const ruleProposalDraftStatusSchema = z.enum([
    /** Official evidence (or a required field) is still missing: cannot be submitted. */
    "EVIDENCE_REQUIRED",
    /** Evidence is attached and every required field is filled: can be submitted. */
    "READY",
    /** Submitted as a rule proposal (PENDING_REVIEW); `proposalId` is set. */
    "SUBMITTED",
    /** Discarded by an operator, with a reason. Keeps its key (re-runs skip it). */
    "DISCARDED",
]);
export const ipiExcludedRouteRuleSchema = z
    .object({
    ruleId: z.string().min(1),
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    originUf: ufSchema.nullable(),
    destinationUf: ufSchema.nullable(),
    validFrom: dateSchema,
    validTo: dateSchema.nullable(),
    /** The route rule's stated reason for excluding IPI. */
    ipiReason: z.string().max(2_000).nullable(),
    kind: ipiExclusionKindSchema,
    /** The rule that states IPI for this route under tax-family-v1 (the TIPI row). */
    tipi: z
        .object({
        ruleId: z.string().min(1),
        treatment: z.enum(["TAXED", "EXEMPT"]),
        rate: rateSchema.nullable(),
    })
        .strict()
        .nullable(),
    /** An IPI restatement draft already prepared for this rule in the draft ruleset. */
    draftId: z.string().uuid().nullable(),
    draftStatus: ruleProposalDraftStatusSchema.nullable(),
})
    .strict();
export const POLICY_READINESS_MAX_VIOLATIONS = 50;
export const POLICY_READINESS_MAX_IPI_ITEMS = 500;
/** `GET /v1/rulesets/{rulesetId}/policy-readiness?policyVersion=` (READ:TAX_RULE:ALL). */
export const policyReadinessSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    rulesetId: z.string().uuid(),
    rulesetVersion: z.string().min(1),
    rulesetStatus: z.string().min(1),
    currentPolicyVersion: z.string().min(1),
    policyVersion: taxPolicyVersionSchema,
    /** Content violations under the target policy: a draft cannot adopt it until they are fixed. */
    blocking: z
        .object({
        count: z.number().int().nonnegative(),
        violations: z
            .array(z.object({ path: z.string(), message: z.string() }).strict())
            .max(POLICY_READINESS_MAX_VIOLATIONS),
    })
        .strict(),
    /** ADR 0011: route rules whose IPI `EXCLUDED` changes meaning under tax-family-v1. */
    ipiExcluded: z
        .object({
        total: z.number().int().nonnegative(),
        review: z.number().int().nonnegative(),
        placeholder: z.number().int().nonnegative(),
        items: z.array(ipiExcludedRouteRuleSchema).max(POLICY_READINESS_MAX_IPI_ITEMS),
        truncated: z.boolean(),
    })
        .strict(),
})
    .strict();
/** `POST /v1/rulesets/{rulesetId}/ipi-restatements` (CREATE:TAX_RULE:ALL, Idempotency-Key). */
export const ipiRestatementRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    ruleIds: z.array(z.string().min(1).max(100)).min(1).max(200),
    reason: reasonSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (new Set(value.ruleIds).size !== value.ruleIds.length) {
        context.addIssue({ code: "custom", path: ["ruleIds"], message: "Must be unique" });
    }
});
/* -- Rule proposal drafts ---------------------------------------------------- */
export const ruleProposalDraftOriginSchema = z.enum([
    "FISCAL_FACT",
    "IPI_RESTATEMENT",
    /** Prefilled from an NF-e observed path (corroboration only, never a legal source). */
    "NFE_OBSERVATION",
]);
/** Official documents to capture and approve in Documentos before submission. */
export const ruleDraftChecklistCodeSchema = z.enum([
    /** Conv. ICMS 142/18, the annex of the CEST segment. */
    "CONV_142_18_ANNEX",
    /** The convênio or protocolo that binds origin and destination for the segment. */
    "ST_AGREEMENT",
    /** RICMS of the destination UF, ST annex item for the NCM/CEST. */
    "RICMS_DESTINATION",
    /** Destination internal rate: a VERIFIED internal-rate fact or the RICMS article. */
    "INTERNAL_RATE",
    /** Senate Resolution 22/89 (12 % / 7 %); 13/2012 (4 %) for imported goods. */
    "INTERSTATE_RATE",
    /** IPI restatement: the act that grants the IPI exemption (TIPI note, law, decree). */
    "IPI_EXEMPTION_ACT",
]);
export const ruleDraftChecklistItemSchema = z
    .object({
    code: ruleDraftChecklistCodeSchema,
    /** Interpolation values for the localized label (segment, annex, UFs, CEST, hint). */
    params: z.record(z.string().min(1).max(40), z.string().max(200)),
    required: z.boolean(),
})
    .strict();
export const ruleDraftFlagSchema = z.enum([
    /** No VERIFIED internal-rate fact: the operator must fill it from the RICMS. */
    "INTERNAL_RATE_MISSING",
    /** The fact states an interstate rate different from Res. Senado 22/89. */
    "FACT_INTERSTATE_DIFFERS",
    /** Imported goods take 4 % (Res. Senado 13/2012): confirm the goods' origin. */
    "IMPORTED_GOODS_CHECK",
    /** Origin equals destination: an internal operation, no interstate rate. */
    "INTERNAL_OPERATION",
    /** Draft is tax-family-v1: IPI stays EXCLUDED and comes from the TIPI row. */
    "IPI_FROM_TIPI",
    /** Draft is legacy: the TIPI row's IPI was copied so the route keeps its IPI. */
    "IPI_COPIED_FROM_TIPI",
    /** Draft is legacy and no TIPI row states IPI: IPI is left EXCLUDED. */
    "IPI_NOT_STATED",
    /** The fact says no ST: expressed as ST with stApplicable false (ADR 0013 open decision 3). */
    "NO_ST_EXPRESSION",
    /** The drifting rule is a route rule for the same key: the proposal supersedes it. */
    "SUPERSEDES_ROUTE_RULE",
    /** Several certified facts share this key; one proposal covers them. */
    "MERGED_FACTS",
    /**
     * Prefilled from NF-e observations: invoices corroborate, they are not a
     * legal source. Rates stay empty until read from the approved evidence.
     */
    "NFE_CORROBORATION_ONLY",
]);
export const ruleDraftMissingFieldSchema = z.enum([
    "evidence",
    "originalMva",
    "destinationInternalRate",
    "interstateRate",
]);
/**
 * Corroborating observation attached to a draft. `NFE_OBSERVATION`: an
 * observed tax path from uploaded NF-e (Regras → Verificação → Notas
 * fiscais). It is never evidence: the draft still needs official documents
 * approved in Documentos before it can be submitted.
 */
export const ruleDraftCorroborationSchema = z
    .object({
    type: z.literal("NFE_OBSERVATION"),
    pathId: z.string().uuid(),
    pathKey: z.string().regex(/^[a-f0-9]{64}$/),
    /** Comparison status when the draft was created. */
    status: z.enum(["DIVERGES", "NOT_COVERED"]),
    cfop: z.string().regex(/^[1-7]\d{3}$/),
    invoiceCount: z.number().int().positive(),
    itemCount: z.number().int().positive(),
    emitterCount: z.number().int().nonnegative(),
    firstIssuedAt: z.string().datetime({ offset: true }),
    lastIssuedAt: z.string().datetime({ offset: true }),
    /** Observed values (decimal fractions) and the platform values they differed from. */
    observed: z.record(z.string().min(1).max(40), z.union([z.string().max(40), z.boolean(), z.null()])),
    divergences: z
        .array(z
        .object({
        field: z.string().min(1).max(40),
        expected: z.union([z.string().max(40), z.boolean()]),
        observed: z.union([z.string().max(40), z.boolean(), z.null()]),
        source: z.string().min(1).max(40),
    })
        .strict())
        .max(5),
    /** Always false: an invoice corroborates; it is not a legal source. */
    legalSource: z.literal(false),
})
    .strict();
export const ruleProposalDraftSchema = z
    .object({
    id: z.string().uuid(),
    revision: z.number().int().positive(),
    origin: ruleProposalDraftOriginSchema,
    status: ruleProposalDraftStatusSchema,
    targetRulesetId: z.string().uuid(),
    targetRulesetVersion: z.string().min(1),
    targetPolicyVersion: z.string().min(1),
    /** Idempotency key within the draft ruleset: `fact:<id>`, `route:<key>` or `rule:<id>`. */
    sourceKey: z.string().min(1).max(300),
    /** Evidence group: `<CEST segment>×<destination UF>` (one document can cover the group). */
    groupKey: z.string().min(1).max(40),
    operation: z.enum(["UPSERT", "SUPERSEDE"]),
    supersedesRuleId: z.string().min(1).nullable(),
    ncm: ncmSchema,
    cest: cestSchema.nullable(),
    originUf: ufSchema.nullable(),
    destinationUf: ufSchema.nullable(),
    /** The prefilled fiscal rule. `evidence` stays empty until submission. */
    proposedRule: z.record(z.string(), z.unknown()),
    factIds: z.array(z.string().uuid()).max(50),
    findingIds: z.array(z.string().uuid()).max(50),
    missingFields: z.array(ruleDraftMissingFieldSchema).max(4),
    flags: z.array(ruleDraftFlagSchema).max(12),
    checklist: z.array(ruleDraftChecklistItemSchema).max(12),
    /** Corroborating observations (NF-e). Never evidence. Absent on older drafts. */
    corroboration: z.array(ruleDraftCorroborationSchema).max(5).optional(),
    evidenceIds: z.array(z.string().uuid()).max(20),
    reason: z.string().min(1).max(2_000),
    proposalId: z.string().uuid().nullable(),
    discardReason: z.string().max(1_000).nullable(),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
})
    .strict();
export const ruleProposalDraftPageSchema = z
    .object({
    items: z.array(ruleProposalDraftSchema).max(200),
    nextCursor: z.string().min(1).nullable(),
    hasMore: z.boolean(),
    totalEstimate: z.number().int().nonnegative(),
})
    .strict();
export const FACT_PROPOSAL_MAX_FACTS = 200;
/**
 * `POST /v1/fiscal-facts/rule-proposal-drafts` (CREATE:TAX_RULE:ALL,
 * Idempotency-Key). `DRIFT` takes every drift row of Verificação →
 * Divergências (capped); `FACTS` takes explicit CERTIFIED/VERIFIED facts.
 */
export const factProposalRequestSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    selector: z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("DRIFT") }).strict(),
        z
            .object({
            kind: z.literal("FACTS"),
            factIds: z.array(z.string().uuid()).min(1).max(FACT_PROPOSAL_MAX_FACTS),
        })
            .strict(),
    ]),
})
    .strict();
export const factProposalSkipReasonSchema = z.enum([
    "NOT_FOUND",
    /** Only CERTIFIED or VERIFIED ST_ROUTE facts become proposals. */
    "NOT_ELIGIBLE",
    /** A draft for this fact already exists in the target draft ruleset. */
    "ALREADY_DRAFTED",
]);
export const factProposalResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    targetRulesetId: z.string().uuid(),
    targetRulesetVersion: z.string().min(1),
    created: z.number().int().nonnegative(),
    existing: z.number().int().nonnegative(),
    skipped: z
        .array(z.object({ factId: z.string().uuid(), reason: factProposalSkipReasonSchema }).strict())
        .max(FACT_PROPOSAL_MAX_FACTS),
    /** Drafts created or found for the requested facts. */
    drafts: z.array(ruleProposalDraftSchema).max(FACT_PROPOSAL_MAX_FACTS),
})
    .strict();
/** Same shape for IPI restatements. */
export const ipiRestatementResultSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    targetRulesetId: z.string().uuid(),
    targetRulesetVersion: z.string().min(1),
    created: z.number().int().nonnegative(),
    existing: z.number().int().nonnegative(),
    skipped: z
        .array(z
        .object({
        ruleId: z.string().min(1),
        reason: z.enum(["NOT_FOUND", "NOT_IPI_EXCLUDED_ROUTE_RULE", "ALREADY_DRAFTED"]),
    })
        .strict())
        .max(200),
    drafts: z.array(ruleProposalDraftSchema).max(200),
})
    .strict();
/**
 * `POST /v1/rule-proposal-drafts/{draftId}/submit` (CREATE:TAX_RULE:ALL,
 * If-Match, Idempotency-Key). The evidence must be APPROVED in Documentos.
 */
export const ruleProposalDraftSubmitSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    evidenceIds: z.array(z.string().uuid()).min(1).max(20),
    /** Required when the draft misses it (no VERIFIED internal-rate fact). */
    destinationInternalRate: rateSchema.optional(),
    /** Required when the draft misses it (internal operation). */
    interstateRate: rateSchema.optional(),
    /** Required when the fact stated ST without an original MVA. */
    originalMva: rateSchema.optional(),
    /**
     * Effective internal burden (carga efetiva) of the destination substitute,
     * used only as ALQ_intra in the MVA adjustment under tax-family-v2. Needs
     * its legal basis; omit both to leave the nominal rate in force.
     */
    effectiveInternalRate: rateSchema.optional(),
    effectiveInternalRateBasis: effectiveInternalRateBasisSchema.optional(),
    reason: reasonSchema.optional(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.effectiveInternalRate === undefined) !==
        (value.effectiveInternalRateBasis === undefined)) {
        context.addIssue({
            code: "custom",
            path: [
                value.effectiveInternalRate === undefined
                    ? "effectiveInternalRate"
                    : "effectiveInternalRateBasis",
            ],
            message: "effectiveInternalRate and effectiveInternalRateBasis go together",
        });
    }
    if (new Set(value.evidenceIds).size !== value.evidenceIds.length) {
        context.addIssue({ code: "custom", path: ["evidenceIds"], message: "Must be unique" });
    }
});
/** `POST /v1/rule-proposal-drafts/{draftId}/discard` (CREATE:TAX_RULE:ALL, If-Match). */
export const ruleProposalDraftDiscardSchema = z
    .object({
    schemaVersion: z.literal("1.0.0"),
    reason: z.string().trim().min(10).max(1_000),
})
    .strict();
//# sourceMappingURL=rule-drafts.js.map